import { Action, configureStore, ThunkAction } from '@reduxjs/toolkit'
import authReducer from './auth/authSlice'
import modalReducer from './modal/modal'
import toastReducer from './toast/toastSlice'
import globalErrorReducer from './error/globaleErrorSlice'
import mfaReducer from './mfa/mfaSlice'
import templateSelectionReducer from './templateLibrary/templateSelectionSlice'
import meetingSummaryReducer from './meetingSummary/meetingSummary'
import meetingSummaryAgentReducer from './meetingSummaryAgent/meetingSummaryAgent'
import sessionFilterReducer from './sessionFilter/sessionFilter'
import emailHubReducer from './emailHub/emailHub'
import taskReducer from './task/task'
import processDocumentReducer from './processDocument/processDocument'
import streamingChatReducer from './streamingChat/streamingChat'
import crossClientLandingReducer from './crossClient/crossClientLanding'
import crossClientResultsReducer from './crossClient/crossClientResults'
import crossClientActionsReducer from './crossClient/crossClientActions'
import businessEventReducer from './businessEvent/businessEvent'

import { userApi } from './user/user'
import { externalUserApi } from './externalUser/externalUser'
import { roleApi } from './role/role'
import { aiApi } from './ai/ai'
import { authApi } from './auth/auth'
import { metricsApi } from './metrics/metrics'
import { faApi } from '../apps/professional-app/fa/store/fa'
import { planApi } from './plan/plan'
import { errorMiddleware } from './middleware/errorMiddleware'
import { sentryErrorMiddleware } from './middleware/sentryMiddleware'
import { accountApi } from './account/account'
import { mediaApi } from './media/media'
import { aiExecuteApi } from './ai/aiExecute'
import { accountUserSettingsApi } from './accountUserSettings/accountUserSettings'
import { accountVocabularyApi } from './accountVocabulary/accountVocabulary'
import { integrationCatalogApi } from './integrationCatalog/integrationCatalog'
import { accountsFileApi, fileApi } from './media/file'
import { integrationsApi } from './integrations/integrations'
import { externalDataSourcesApi } from './integrations/externalDataSources'
import { customIntegrationApi } from './customIntegration/customIntegration'
import { meetingTemplateApi } from './ai/meetingTemplate'
import { meetingSupportApi } from './meetingSupport/meetingSupport'
import { syncApi } from './sync/sync'
import { inputParametersHierarchyApi } from './ai/inputParametersHierarchy'
import { quickFilter } from './sessionFilter/quickFilter'
import { aiAgentApi } from './ai/aiAgent'
import { aiAgentResultApi } from './ai/aiAgentResult'
import { aiAgentModelApi } from './ai/aiAgentModel'
import { userTemporaryCodeApi } from './userTemporaryCode/userTemporaryCode'
import { mfaApi } from './mfa/mfa'
import { webNotificationsApi } from './webNotifications/webNotifications'
import { emailSubscriptionsApi } from './emailSubscriptions/emailSubscriptions'
import { scorecardApi } from './ai/scorecard'
import { eventFilterApi } from './eventFilter/eventFilter'
import { insightsApi } from '../shared/insights/store/insights'
import { questionInsightsApi } from './questionInsights/questionInsights'
import { emailTypeApi } from './integrations/emailTypes'
import { emailFilterApi } from './integrations/emailFilters'
import { emailMetadataApi } from './integrations/emailMetadata'
import { setupListeners } from '@reduxjs/toolkit/query'
import liveMeetingsReducer from './liveMeetings/liveMeetings'
import { findAndReplaceApi } from './meetingSummary/findAndReplace'
import { crossAccountApi } from './crossAccount/crossAccount'
import importedForm from './formImport/importedForm'
import { documentTypeApi } from './ai/documentTypes'
import { agendaApi } from './agenda/agenda'
import { enabledWorkflowApi } from './workflow/enabledWorkflows'
import { crossClientExecutionApi } from './crossClient/crossClientExecution'
import { crossClientApi } from './crossClient/crossClient'
import { scheduledCrossClientApi } from './crossClient/scheduledCrossClient'
import { promptSegmentApi } from './ai/promptSegment'
import { formTemplateApi } from './ai/formTemplate'
import { businessEventApi } from './ai/businessEvent'
import { templateLibraryApi } from './templateLibrary/templateLibrary'
import { permissionsApi } from './permissions/permissions'
import { globalConfigsApi } from './globalConfigs/globalConfigs'
import { documentConnectorsApi } from './documentConnectors/documentConnectors'
import { accountOpeningApi } from './accountOpening/accountOpening'
import documentFileBrowserReducer from './documentFileBrowser/documentFileBrowserSlice'
import { onboardingApi } from './onboarding/onboarding'
import onboardingUiReducer from './onboarding/onboardingUiSlice'
import { noticesApi } from './notices/notices'
import { walletApi } from './wallet/wallet'
import { schedulingApi } from './scheduling/scheduling'
import { documentsApi } from './ai/documents'
import { signupChangeApi } from './signupChange/signupChange'
import { customWorkflowsApi } from './customWorkflow/customWorkflows'
import { redtailApi } from './redtail/redtail'

export const store = configureStore({
  reducer: {
    auth: authReducer,
    toast: toastReducer,
    globalError: globalErrorReducer,
    meetingSummary: meetingSummaryReducer,
    sessionFilter: sessionFilterReducer,
    meetingSummaryAgent: meetingSummaryAgentReducer,
    mfa: mfaReducer,
    emailHub: emailHubReducer,
    task: taskReducer,
    liveMeetings: liveMeetingsReducer,
    forms: importedForm,
    processDocument: processDocumentReducer,
    streamingChat: streamingChatReducer,
    modal: modalReducer,
    crossClientLanding: crossClientLandingReducer,
    crossClientResults: crossClientResultsReducer,
    crossClientActions: crossClientActionsReducer,
    businessEvent: businessEventReducer,
    templateSelection: templateSelectionReducer,
    documentFileBrowser: documentFileBrowserReducer,
    [userApi.reducerPath]: userApi.reducer,
    [externalUserApi.reducerPath]: externalUserApi.reducer,
    [roleApi.reducerPath]: roleApi.reducer,
    [aiApi.reducerPath]: aiApi.reducer,
    [meetingSupportApi.reducerPath]: meetingSupportApi.reducer,
    [meetingTemplateApi.reducerPath]: meetingTemplateApi.reducer,
    [aiExecuteApi.reducerPath]: aiExecuteApi.reducer,
    [aiAgentApi.reducerPath]: aiAgentApi.reducer,
    [aiAgentModelApi.reducerPath]: aiAgentModelApi.reducer,
    [aiAgentResultApi.reducerPath]: aiAgentResultApi.reducer,
    [authApi.reducerPath]: authApi.reducer,
    [faApi.reducerPath]: faApi.reducer,
    [planApi.reducerPath]: planApi.reducer,
    [metricsApi.reducerPath]: metricsApi.reducer,
    [accountApi.reducerPath]: accountApi.reducer,
    [mediaApi.reducerPath]: mediaApi.reducer,
    [accountUserSettingsApi.reducerPath]: accountUserSettingsApi.reducer,
    [accountVocabularyApi.reducerPath]: accountVocabularyApi.reducer,
    [integrationCatalogApi.reducerPath]: integrationCatalogApi.reducer,
    [fileApi.reducerPath]: fileApi.reducer,
    [integrationsApi.reducerPath]: integrationsApi.reducer,
    [externalDataSourcesApi.reducerPath]: externalDataSourcesApi.reducer,
    [customIntegrationApi.reducerPath]: customIntegrationApi.reducer,
    [permissionsApi.reducerPath]: permissionsApi.reducer,
    [globalConfigsApi.reducerPath]: globalConfigsApi.reducer,
    [syncApi.reducerPath]: syncApi.reducer,
    [inputParametersHierarchyApi.reducerPath]: inputParametersHierarchyApi.reducer,
    [quickFilter.reducerPath]: quickFilter.reducer,
    [accountsFileApi.reducerPath]: accountsFileApi.reducer,
    [userTemporaryCodeApi.reducerPath]: userTemporaryCodeApi.reducer,
    [mfaApi.reducerPath]: mfaApi.reducer,
    [webNotificationsApi.reducerPath]: webNotificationsApi.reducer,
    [emailSubscriptionsApi.reducerPath]: emailSubscriptionsApi.reducer,
    [scorecardApi.reducerPath]: scorecardApi.reducer,
    [insightsApi.reducerPath]: insightsApi.reducer,
    [questionInsightsApi.reducerPath]: questionInsightsApi.reducer,
    [eventFilterApi.reducerPath]: eventFilterApi.reducer,
    [emailTypeApi.reducerPath]: emailTypeApi.reducer,
    [emailFilterApi.reducerPath]: emailFilterApi.reducer,
    [emailMetadataApi.reducerPath]: emailMetadataApi.reducer,
    [findAndReplaceApi.reducerPath]: findAndReplaceApi.reducer,
    [crossAccountApi.reducerPath]: crossAccountApi.reducer,
    [documentTypeApi.reducerPath]: documentTypeApi.reducer,
    [documentsApi.reducerPath]: documentsApi.reducer,
    [agendaApi.reducerPath]: agendaApi.reducer,
    [enabledWorkflowApi.reducerPath]: enabledWorkflowApi.reducer,
    [promptSegmentApi.reducerPath]: promptSegmentApi.reducer,
    [crossClientApi.reducerPath]: crossClientApi.reducer,
    [crossClientExecutionApi.reducerPath]: crossClientExecutionApi.reducer,
    [scheduledCrossClientApi.reducerPath]: scheduledCrossClientApi.reducer,
    [formTemplateApi.reducerPath]: formTemplateApi.reducer,
    [businessEventApi.reducerPath]: businessEventApi.reducer,
    [templateLibraryApi.reducerPath]: templateLibraryApi.reducer,
    [documentConnectorsApi.reducerPath]: documentConnectorsApi.reducer,
    [accountOpeningApi.reducerPath]: accountOpeningApi.reducer,
    [onboardingApi.reducerPath]: onboardingApi.reducer,
    onboardingUi: onboardingUiReducer,
    [noticesApi.reducerPath]: noticesApi.reducer,
    [walletApi.reducerPath]: walletApi.reducer,
    [schedulingApi.reducerPath]: schedulingApi.reducer,
    [signupChangeApi.reducerPath]: signupChangeApi.reducer,
    [customWorkflowsApi.reducerPath]: customWorkflowsApi.reducer,
    [redtailApi.reducerPath]: redtailApi.reducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(
      userApi.middleware,
      externalUserApi.middleware,
      roleApi.middleware,
      aiApi.middleware,
      meetingSupportApi.middleware,
      meetingTemplateApi.middleware,
      aiExecuteApi.middleware,
      aiAgentApi.middleware,
      aiAgentModelApi.middleware,
      aiAgentResultApi.middleware,
      authApi.middleware,
      metricsApi.middleware,
      faApi.middleware,
      planApi.middleware,
      accountApi.middleware,
      mediaApi.middleware,
      accountUserSettingsApi.middleware,
      accountVocabularyApi.middleware,
      integrationCatalogApi.middleware,
      fileApi.middleware,
      accountsFileApi.middleware,
      integrationsApi.middleware,
      externalDataSourcesApi.middleware,
      customIntegrationApi.middleware,
      permissionsApi.middleware,
      globalConfigsApi.middleware,
      syncApi.middleware,
      inputParametersHierarchyApi.middleware,
      quickFilter.middleware,
      userTemporaryCodeApi.middleware,
      mfaApi.middleware,
      webNotificationsApi.middleware,
      emailSubscriptionsApi.middleware,
      scorecardApi.middleware,
      eventFilterApi.middleware,
      insightsApi.middleware,
      questionInsightsApi.middleware,
      emailTypeApi.middleware,
      emailFilterApi.middleware,
      emailMetadataApi.middleware,
      findAndReplaceApi.middleware,
      crossAccountApi.middleware,
      documentTypeApi.middleware,
      documentsApi.middleware,
      agendaApi.middleware,
      enabledWorkflowApi.middleware,
      crossClientApi.middleware,
      crossClientExecutionApi.middleware,
      scheduledCrossClientApi.middleware,
      promptSegmentApi.middleware,
      formTemplateApi.middleware,
      businessEventApi.middleware,
      templateLibraryApi.middleware,
      documentConnectorsApi.middleware,
      accountOpeningApi.middleware,
      onboardingApi.middleware,
      noticesApi.middleware,
      walletApi.middleware,
      schedulingApi.middleware,
      signupChangeApi.middleware,
      customWorkflowsApi.middleware,
      redtailApi.middleware,
      errorMiddleware,
      sentryErrorMiddleware,
    ),
})

setupListeners(store.dispatch)

export type AppDispatch = typeof store.dispatch
export type RootState = ReturnType<typeof store.getState>
export type AppThunk<ReturnType = void> = ThunkAction<ReturnType, RootState, unknown, Action<string>>
